import {patchState, signalStore, withLinkedState, withMethods, withState} from '@ngrx/signals';
import {withDevtools} from '@angular-architects/ngrx-toolkit';
import {inject} from '@angular/core';
import {ApiService} from '../../../shared/services/api.service';
import {catchError, concatMap, finalize, firstValueFrom, from, map, Observable, of, take, tap, toArray} from 'rxjs';
import {RouterService} from '../../../shared/services/router.service';
import {LanguageService} from '../../../shared/services/language.service';
import {ToasterService} from '../../../shared/services/toaster.service';
import {ChatListResource} from '../../../shared/types/chat-types/chatListResource';
import {ChatListFilterType} from '../../../shared/types/chat-types/chatListFilterType';
import {ChatUpdateRequest} from '../../../shared/types/chat-types/chatUpdateRequest';
import {ChatResource} from '../../../shared/types/chat-types/chatResource';
import {ProfileService} from '../../../shared/services/profile.service';
import {ChatMessageResource} from '../../../shared/types/chat-types/chatMessageResource';
import {ChatMessageCursorType} from '../../../shared/types/chat-types/chatMessageCursorType';
import {HttpResponse} from '@angular/common/http';
import {ChatMessageReaderResource} from '../../../shared/types/chat-types/chatMessageReaderResource';
import {UserContactResource} from '../../../shared/types/contact-types/userContactResource';
import {ChatShareCounts} from '../../../shared/types/chat-types/chatShareCounts';
import {AddNewChatMessagePayload} from '../../../shared/types/chat-types/addNewChatMessagePayload';

type ActiveChatReloadMode = 'route-target' | 'full' | 'newer';

export interface ChatState {
  chatModuleOpen: boolean;
  activeChat: number;
  activeMessage: number;
  activeChatDetails: ChatResource | null;
  activeChatMessages: ChatMessageResource[];
  activeChatMessagesMetadata: {
    limit: number;
    search: string;
    hasMoreNewer: boolean;
    hasMoreOlder: boolean;
  },
  activeChatMetadata: {
    admin: number;
    participants: number[];
    replyMessageId: number;
    editMessageId: number;
    finalScrollEnd: boolean;
    finalScrollStart: boolean;
  },
  activeChatAdmin: UserContactResource | null,
  activeChatShareCounts: ChatShareCounts | null
  chatList: ChatListResource[];
  chatListMetadata: {
    limit: number;
    skip: number
    query: string;
    category: ChatListFilterType | null;
    activeChatsAvailable: boolean;
  },
  selectedChatMessages: number[],
  chatDrafts: Map<number, string>,
  chatSearch: {
    query: string,
    page: number,
  }
}

const initialState: ChatState = {
  chatModuleOpen: false,
  activeChat: 0,
  activeMessage: 0,
  activeChatDetails: null,
  activeChatMessages: [],
  activeChatMessagesMetadata: {
    limit: 25,
    search: '',
    hasMoreNewer: true,
    hasMoreOlder: true,
  },
  activeChatMetadata: {
    admin: 0,
    participants: [],
    replyMessageId: 0,
    editMessageId: 0,
    finalScrollEnd: false,
    finalScrollStart: false,
  },
  activeChatAdmin: null,
  activeChatShareCounts: null,
  chatList: [],
  chatListMetadata: {
    limit: 25,
    skip: 0,
    query: '',
    category: null,
    activeChatsAvailable: true
  },
  selectedChatMessages: [],
  chatDrafts: new Map<number, string>(),
  chatSearch: {
    query: '',
    page: 0,
  }
};

export const ChatStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('ChatStore'),
  withLinkedState((store) => {
    return {
      selectedMessages() {
        return store.activeChatMessages().filter((message) => store.selectedChatMessages().includes(message.id));
      },
      replyMessage() {
        return store.activeChatMessages().find((message) => message.id === store.activeChatMetadata.replyMessageId());
      },
    }
  }),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const router: RouterService = inject(RouterService);
    const languageService: LanguageService = inject(LanguageService);
    const toasterService: ToasterService = inject(ToasterService);
    const profile: ProfileService = inject(ProfileService);
    const LOCAL_REACTION_UPDATE_TTL_MS = 10_000;
    const pendingLocalReactionUpdates = new Map<number, {count: number; timestamp: number}>();
    const pendingChatMessagePaginationRequests = new Set<string>();
    const getChatSortTimestamp = (chat: ChatListResource): number =>
      new Date(chat.latest_message?.created_at ?? chat.created_at).getTime() || 0;

    const sortChatListWithPinnedPriority = (chatList: ChatListResource[]): ChatListResource[] =>
      [...chatList].sort((a, b) => {
        if (a.favorite !== b.favorite) {
          return a.favorite ? -1 : 1;
        }
        return getChatSortTimestamp(b) - getChatSortTimestamp(a);
      });

    const dedupeMessagesById = (messages: ChatMessageResource[]): ChatMessageResource[] => {
      const seenIds = new Set<number>();
      return messages.filter((message) => {
        if (seenIds.has(message.id)) {
          return false;
        }
        seenIds.add(message.id);
        return true;
      });
    };

    const queuePendingLocalReactionUpdate = (messageId: number): void => {
      const currentPending = pendingLocalReactionUpdates.get(messageId);
      pendingLocalReactionUpdates.set(messageId, {
        count: (currentPending?.count ?? 0) + 1,
        timestamp: Date.now()
      });
    };

    const removePendingLocalReactionUpdate = (messageId: number): void => {
      const currentPending = pendingLocalReactionUpdates.get(messageId);
      if (!currentPending) {
        return;
      }

      if (currentPending.count <= 1) {
        pendingLocalReactionUpdates.delete(messageId);
      } else {
        pendingLocalReactionUpdates.set(messageId, {
          count: currentPending.count - 1,
          timestamp: currentPending.timestamp
        });
      }
    };

    const consumePendingLocalReactionUpdate = (messageId: number): boolean => {
      const currentPending = pendingLocalReactionUpdates.get(messageId);
      if (!currentPending) {
        return false;
      }

      const isExpired = Date.now() - currentPending.timestamp > LOCAL_REACTION_UPDATE_TTL_MS;
      if (isExpired) {
        pendingLocalReactionUpdates.delete(messageId);
        return false;
      }

      removePendingLocalReactionUpdate(messageId);
      return true;
    };

    return {
      setChatModuleOpen(isOpen: boolean): void {
        patchState(store, (state) => ({
          ...state,
          chatModuleOpen: isOpen,
        }));
      },
      resetStore(): void {
        pendingLocalReactionUpdates.clear();
        pendingChatMessagePaginationRequests.clear();
        patchState(store, {
          ...initialState,
          chatDrafts: new Map<number, string>(),
        });
      },
      async setActiveChatAndMessage(chat_id: number, message_id: number){
        const isActiveChatChanged = store.activeChat() !== chat_id;

        patchState(store, (state) => ({
          ...state,
          activeChat: chat_id,
          activeMessage: message_id,
          ...(isActiveChatChanged ? {
            activeChatMessages: [],
            activeChatMessagesMetadata: initialState.activeChatMessagesMetadata,
          } : {})
        }));

        if(chat_id !== 0) {
          if (isActiveChatChanged) {
            await this.markChatReadAndReload(chat_id, {
              messageId: message_id,
              reloadMode: 'route-target',
              loadDetails: true,
              resetSelection: true,
            });
          } else {
            this.patchChatListItemReadOnOpen(chat_id);
            this.loadChatDetails();
            this.loadChatMessages(message_id === 0 ? null: message_id, (message_id === 0) ? null : ChatMessageCursorType.Around);
            this.resetChatMessageSelection();
          }
        } else {
          patchState(store, (state) => 
        ({
          ...state,
          activeChatDetails: initialState.activeChatDetails,
        }));
        }
      },
      clearActiveMessage(): void {
        patchState(store, (state) => ({
          ...state,
          activeMessage: 0,
        }));
      },
      setActiveMessage(message_id: number): void {
        patchState(store, (state) => ({
          ...state,
          activeMessage: message_id,
        }));
      },
      clearActiveChatSelection(): void {
        patchState(store, (state) => ({
          ...state,
          activeChat: 0,
          activeMessage: 0,
          activeChatDetails: initialState.activeChatDetails,
          activeChatMetadata: initialState.activeChatMetadata,
          activeChatAdmin: initialState.activeChatAdmin,
          activeChatShareCounts: initialState.activeChatShareCounts,
          selectedChatMessages: initialState.selectedChatMessages,
        }));
      },
      //#region chat list
      loadChatList(){
        const isFirstPageLoad = store.chatListMetadata.skip() === 0;
        api.getChatList(store.chatListMetadata.category(), store.chatListMetadata.query(), store.chatListMetadata.limit(), store.chatListMetadata.skip())
        .pipe(take(1))
        .subscribe({
          next: (chats) => {
            if (isFirstPageLoad) {
              this.setChatList(chats);
            } else {
              this.addChatsToList(chats);
            }
            this.updateChatListPagination(chats.length);
            this.setActiveChatsAvailable();
          },
          error: (error) => {
            console.error('Error loading chat list: ', error);
          }
        });
      },
      reloadChatList() {
        patchState(store, (state) => ({
          ...state,
          chatListMetadata: {
            ...state.chatListMetadata,
            skip: 0,
          },
        }));
        this.loadChatList();
      },
      addChatsToList(chats: ChatListResource[]){
        patchState(store, (state) => {
          const newChatList = [...state.chatList, ...chats];
          const newUniqueChats = newChatList.filter((chat, index, self) =>
            index === self.findIndex((c) => c.id === chat.id)
          );
          return {
            ...state,
            chatList: newUniqueChats
          };
        });
      },
      setChatList(chats: ChatListResource[]){
        patchState(store, (state) => 
        ({
          ...state,
          chatList: chats
        })
        )
      },
      patchChatListItemReadOnOpen(chatId: number): void {
        patchState(store, (state) => ({
          ...state,
          chatList: state.chatList.map((chat) =>
            chat.id === chatId && chat.latest_message && chat.latest_message.read === false
              ? {
                ...chat,
                latest_message: {
                  ...chat.latest_message,
                  read: true
                }
              }
              : chat
          )
        }));
      },
      async markChatReadOnOpen(chatId: number): Promise<void> {
        try {
          await firstValueFrom(api.markChatRead(chatId).pipe(take(1)));
        } catch (error) {
          console.error('Error marking chat as read: ', error);
        }
      },
      async markChatReadAndReload(
        chatId: number,
        options: {
          messageId?: number;
          reloadMode?: ActiveChatReloadMode;
          loadDetails?: boolean;
          resetSelection?: boolean;
        } = {}
      ): Promise<void> {
        if (chatId === 0) {
          return;
        }

        const {
          messageId = 0,
          reloadMode = 'full',
          loadDetails = true,
          resetSelection = false,
        } = options;

        await this.markChatReadOnOpen(chatId);

        if (store.activeChat() !== chatId) {
          return;
        }

        this.patchChatListItemReadOnOpen(chatId);

        if (loadDetails) {
          this.loadChatDetails();
        }

        switch (reloadMode) {
          case 'route-target':
            this.loadChatMessages(messageId === 0 ? null : messageId, messageId === 0 ? null : ChatMessageCursorType.Around);
            break;
          case 'newer':
            patchState(store, (state) => ({
              ...state,
              activeChatMessagesMetadata: {
                ...state.activeChatMessagesMetadata,
                hasMoreNewer: true,
              }
            }));
            if (store.activeChatMessages().length === 0) {
              this.loadChatMessages(null, null);
            } else {
              this.loadAdditionalChatMessages('newer');
            }
            break;
          default:
            this.loadChatMessages(null, null);
            break;
        }

        if (resetSelection) {
          this.resetChatMessageSelection();
        }
      },
      updateChatListPagination(page: number){
        patchState(store, (state) => 
        ({
          ...state,
          chatListMetadata: {
            ...state.chatListMetadata,
            skip: state.chatListMetadata.skip + page
          }
        })
        )
      },
      setChatCategory(category: ChatListFilterType | null){
        patchState(store, (state) => 
        ({
          ...state,
          chatListMetadata: {
            ...state.chatListMetadata,
            category: category,
          }
        })
        );
        this.reloadChatList();
      },
      setChatQuery(query: string){
        patchState(store, (state) => 
        ({
          ...state,
          chatListMetadata: {
            ...state.chatListMetadata,
            query: query,
          }
        }));
        this.reloadChatList();
      },
      hasUnreadChats(): Observable<boolean> {
        return api.getChatList(ChatListFilterType.Unread, '', 1, 0)
        .pipe(
          take(1),
          map((chats: ChatListResource[]) => chats.length > 0),
          catchError((error) => {
            console.error('Error checking unread chats: ', error);
            return of(true);
          })
        );
      },
      setActiveChatsAvailable(){
        if(store.chatListMetadata.query() === '' && store.chatListMetadata.category() === null){
          patchState(store, (state) => 
          ({
            ...state,
            chatListMetadata: {
              ...state.chatListMetadata,
              activeChatsAvailable: !!store.chatList().length
            }
          })
          );
        }
      },
      loadChatDetailsForChatListItem(chatId: number): Observable<ChatResource | null> {
        return api.getChatListItem(chatId)
        .pipe(
          take(1),
          catchError((error) => {
            console.error('Error loading chat details for chat list item: ', error);
            return of(null);
          })
        );
      },
      //#endregion chat list
      //#region chat details
      loadChatDetails(): void {
        if(store.activeChat() !== 0) {
          this.loadChatDetailsForChatListItem(store.activeChat()!)
          .pipe(take(1))
          .subscribe({
            next: (chatDetails) => {
              patchState(store, (state) => 
                ({
                  ...state,
                  activeChatDetails: chatDetails
                })
              );
              this.loadActiveChatAdmin();
              this.loadActiveChatShareInfo();
            },
          });
        }
      },
      loadActiveChatAdmin(): void {
        const chatDetails = store.activeChatDetails();
        const users = chatDetails?.users ?? [];
        const adminUsers = chatDetails?.admin_users ?? [];

        const adminId = Number(
          users.length === 1
            ? users[0]?.id
            : users.length <= 2
              ? users.find((user) => user.id !== profile.profile()?.user.id)?.id
              : adminUsers[0]?.id
        );

        if (!Number.isFinite(adminId) || adminId <= 0) {
          patchState(store, (state) =>
          ({
            ...state,
            activeChatAdmin: null
          })
          );
          return;
        }

        api.getContact(adminId)
        .pipe(take(1))
        .subscribe({
          next: (adminContact) => {
            patchState(store, (state) =>
            ({
              ...state,
              activeChatAdmin: adminContact
            })
            );
          },
          error(error) {
            console.error('Error loading active chat admin contact: ', error);
          }
        })
      },
      loadActiveChatShareInfo(): void {
        api.getChatListItemShareCounts(store.activeChat())
        .pipe(take(1))
        .subscribe({
          next: (shareCounts) => {
            patchState(store, (state) =>
            ({
              ...state,
              activeChatShareCounts: shareCounts
            })
            );
          },
          error: (error) => {
            console.error(error);
          }
        })
      },
      //#endregion chat details
      //#region chat drafts
      updateChatDraft(chatId: number, draft: string){
        const drafts = store.chatDrafts();
        drafts.set(chatId, draft);
        patchState(store, (state) => 
        ({
          ...state,
          chatDrafts: new Map(drafts) // Create a new Map to ensure immutability for change detection
        })
        );
      },
      removeChatDraft(chatId: number){
        const drafts = store.chatDrafts();
        drafts.delete(chatId);
        patchState(store, (state) => 
        ({
          ...state,
          chatDrafts: new Map(drafts) // Create a new Map to ensure immutability for change detection
        })
      )
      },
      //#endregion chat drafts
      //#region chat message actions
      loadChatMessages(cursor: number | null, direction: ChatMessageCursorType | null, requestKey: string | null = null): void {
        if(store.activeChat() !== 0) {
          api.getChatMessageList(
            store.activeChat(),
            cursor,
            direction,
            store.activeChatMessagesMetadata().limit,
            store.activeChatMessagesMetadata().search,
          ).pipe(
            take(1),
            finalize(() => {
              if (requestKey) {
                pendingChatMessagePaginationRequests.delete(requestKey);
              }
            })
          )
          .subscribe({
            next: (chatMessagesResponse: HttpResponse<ChatMessageResource[]>) => {
              const chatMessages = chatMessagesResponse.body ?? [];
              const uniqueChatMessages = dedupeMessagesById(chatMessages);
              const headers = chatMessagesResponse.headers;
              
              // if cursor === null --> overwrite list
              // if cursor !== null --> attach messages to existing list depending on direction
              patchState(store, (state) => {
                let updatedMessages: ChatMessageResource[];
                let hasMoreNewer = state.activeChatMessagesMetadata.hasMoreNewer;
                let hasMoreOlder = state.activeChatMessagesMetadata.hasMoreOlder;
                const dedupedStateMessages = dedupeMessagesById(state.activeChatMessages);
  
                if (cursor === null || direction === ChatMessageCursorType.Around) {
                  // Overwrite the list
                  updatedMessages = uniqueChatMessages;
                } else {
                  const existingMessageIds = new Set(dedupedStateMessages.map((message) => message.id));
                  const uniqueIncomingMessages = uniqueChatMessages.filter((message) => !existingMessageIds.has(message.id));

                  if (direction === ChatMessageCursorType.Before) {
                    // Attach older messages at the start
                    updatedMessages = [...uniqueIncomingMessages, ...dedupedStateMessages];
                  } else {
                    // Attach newer messages at the end
                    updatedMessages = [...dedupedStateMessages, ...uniqueIncomingMessages];
                  }
                }
  
                // Update hasMoreNewer and hasMoreOlder from headers if present
                if (headers.has('x-more-entries-before')) {
                  hasMoreOlder = headers.get('x-more-entries-before') === 'true';
                }
                if (headers.has('x-more-entries-after')) {
                  hasMoreNewer = headers.get('x-more-entries-after') === 'true';
                }
  
                return {
                  ...state,
                  activeChatMessages: updatedMessages,
                  activeChatMessagesMetadata: {
                    ...state.activeChatMessagesMetadata,
                    hasMoreOlder,
                    hasMoreNewer,
                  }
                };
              });

              this.checkPatchForLatestMessage(uniqueChatMessages);
            },
            error: (error) => {
              console.error('Error loading chat messages: ', error);
            }
          });
        }
      },
      loadAdditionalChatMessages(direction: 'newer' | 'older'): void {
        const chatId = store.activeChat();
        if (chatId === 0) {
          return;
        }

        const cursor = (direction === 'newer') ?
          store.activeChatMessages()[store.activeChatMessages().length - 1]?.id ?? null :
          store.activeChatMessages()[0]?.id ?? null;
        if (cursor === null) {
          return;
        }

        const dir = (direction === 'newer') ? ChatMessageCursorType.After : ChatMessageCursorType.Before;
        const hasMore = dir === ChatMessageCursorType.After ?
          store.activeChatMessagesMetadata().hasMoreNewer :
          store.activeChatMessagesMetadata().hasMoreOlder;
        if (!hasMore) {
          return;
        }

        const requestKey = `${chatId}:${dir}:${cursor}`;
        if (pendingChatMessagePaginationRequests.has(requestKey)) {
          return;
        }

        pendingChatMessagePaginationRequests.add(requestKey);
        this.loadChatMessages(cursor, dir, requestKey);
      },
      removeMessageForMe(messageIds: number[]): void {
        const uniqueMessageIds = [...new Set(messageIds)];
        api.deleteChatMessageForSelf(store.activeChat(), uniqueMessageIds)
        .pipe(take(1))
        .subscribe({
          next: () => {
            uniqueMessageIds.forEach((messageId) => this.patchMessageDeleted(messageId));
          },
          error: (error) => {
            console.error('Error removing chat message for self: ', error);
          }
        });
      },
      removeMessageForAll(messageIds: number[]): void {
        const uniqueMessageIds = [...new Set(messageIds)];
        api.deleteChatMessageForEveryone(store.activeChat(), uniqueMessageIds)
        .pipe(take(1))
        .subscribe({
          next: () => {
            uniqueMessageIds.forEach((messageId) => this.patchMessageDeleted(messageId));
          },
          error: (error) => {
            console.error('Error removing chat message for self: ', error);
          }
        });
      },
      forwardSelectedMessages(chatIds: number[]): Observable<void> {
        const selectedIds = store.selectedChatMessages();
        const messages = store.activeChatMessages().filter((message) => selectedIds.includes(message.id)) ?? [];
        const nonEmptyTextMessages = messages.filter((message) => (message.message ?? '').trim().length > 0);
        const shouldForwardAsSingleCombinedMessage = messages.length > 1 && nonEmptyTextMessages.length <= 1;

        return from(chatIds).pipe(
          concatMap((chatId) =>
            shouldForwardAsSingleCombinedMessage
              ? api.addChatMessage(
                  chatId,
                  {
                    message: nonEmptyTextMessages[0]?.message ?? null,
                    file_storage_uuids: [...new Set(messages
                      .map((message) => message.file?.uuid)
                      .filter((fileId): fileId is string => !!fileId))],
                    forwarded: true,
                    reply_to: null
                  }
                ).pipe(
                  tap((newMessages: ChatMessageResource[]) => {
                    if (chatId === store.activeChat()) {
                      this.patchNewMessages(newMessages);
                    }
                  })
                )
              : from(messages).pipe(
                  concatMap((message: ChatMessageResource) =>
                    api.addChatMessage(
                      chatId,
                      {
                        message: message.message ?? null,
                        file_storage_uuids: message.file?.uuid ? [message.file.uuid] : [],
                        forwarded: true,
                        reply_to: null
                      }
                    ).pipe(
                      tap((newMessages: ChatMessageResource[]) => {
                        if (chatId === store.activeChat()) {
                          this.patchNewMessages(newMessages);
                        }
                      })
                    )
                  )
                )
          ),
          toArray(),
          tap(() => {
            this.reloadChatList();
          }),
          map(() => void 0),
        );
      },
      setChatMessageFavorite(messageId: number, favorite: boolean): void {
        api.setChatMessageFavorite(store.activeChat(), messageId, favorite)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.patchMessageFavorite(messageId, favorite);
            if (store.chatListMetadata.category() === ChatListFilterType.Favorites) {
              this.setChatCategory(store.chatListMetadata.category());
            }
          },
          error: (error) => {
            console.error('Error settings chat message favorite: ', error);
          }
        })
      },
      setChatMessageReaction(messageId: number, emoji: string): void {
        queuePendingLocalReactionUpdate(messageId);
        api.setChatMessageEmoji(store.activeChat(), messageId, emoji)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.patchMessageEmoji(messageId, emoji);
          },
          error: (error) => {
            removePendingLocalReactionUpdate(messageId);
            console.error('Error setting chat message emoji: ', error);
          }
        });
      },
      reportChatMessage(messageId: number): void {
        api.reportChatMessage(store.activeChat(), messageId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            toasterService.success(
              languageService.getTranslationByKey('CHATREPORTSUCCESS'),
              languageService.getTranslationByKey('CHATREPORTSUCCESSMESSAGE')
            );
          },
          error: (error) => {
            console.error('Error reporting chat message: ', error);
          }
        })
      },
      pinChatMessage(messageId: number, pin: boolean): void {
        api.setChatMessagePin(store.activeChat(), messageId, pin)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.loadChatDetails();
            patchState(store, (state) => ({
              ...state,
              activeChatMessages: state.activeChatMessages.map((message) => ({
                ...message,
                pinned: pin ? message.id === messageId : false
              }))
            }));
          },
          error: (error) => {
            console.error('Error settings chat message favorite: ', error);
          }
        });
      },
      toggleSelectChatMessage(messageId: number): void {
        patchState(store, (state) => 
        ({
          ...state,
          selectedChatMessages: state.selectedChatMessages.includes(messageId) ? state.selectedChatMessages.filter((id) => id !== messageId) : [...state.selectedChatMessages, messageId]
        })
        )
      },
      resetChatMessageSelection(): void {
        patchState(store, (state) => 
        ({
          ...state,
          selectedChatMessages: []
        })
        )
      },
      isMessageSelected(messageId: number): boolean {
        return store.selectedChatMessages().includes(messageId);
      },
      setReplyMessage(messageId: number): void {
        patchState(store, (state) => 
        ({
          ...state,
          activeChatMetadata: {
            ...state.activeChatMetadata,
            replyMessageId: messageId
          }
        })
        )
      },
      removeReplyMessage(): void {
        patchState(store, (state) => 
        ({
          ...state,
          activeChatMetadata: {
            ...state.activeChatMetadata,
            replyMessageId: 0
          }
        })
        )
      },
      setEditMessage(messageId: number): void {
        patchState(store, (state) => 
        ({
          ...state,
          activeChatMetadata: {
            ...state.activeChatMetadata,
            editMessageId: messageId
          }
        })
        )
      },
      removeEditMessage(): void {
        patchState(store, (state) => 
        ({
          ...state,
          activeChatMetadata: {
            ...state.activeChatMetadata,
            editMessageId: 0
          }
        })
        )
      },
      addNewMessage(payload: AddNewChatMessagePayload): Observable<ChatMessageResource[]> {
        if(payload.message !== '' || payload.media.length !== 0 || payload.audio.length !== 0 || payload.files.length !== 0){
          return api.addChatMessage(
            store.activeChat(),
              {
                message: payload.message !== '' ? payload.message : null,
                file_storage_uuids: [...payload.media, ...payload.audio, ...payload.files].map((file) => file.uuid),
                forwarded: false,
                reply_to: payload.replyTo
              }
            )
            .pipe(
            take(1),
            catchError((error) => {
              console.error('Error adding new chat message: ', error);
              return of([]);
  
            }),
            tap((messages: ChatMessageResource[]) => {
              this.patchNewMessages(messages);
            })
          );
        } else {
          return of([]);
        }
      },
      updateMessage(messageId: number, message: string | null): void {
        api.updateChatMessage(store.activeChat(), messageId, message)
        .pipe(take(1))
        .subscribe({
          next: (data: ChatMessageResource) => {
            const updatedMessage = {
              ...data,
              link: null
            };

            this.removeEditMessage();
            this.patchUpdatedMessage(updatedMessage);
            this.patchMessageToReply(updatedMessage);
            this.patchMessageToLatestMessage(updatedMessage);

            toasterService.success(
              languageService.getTranslationByKey('CHATMESSAGEUPDATESUCCESSTITLE'),
              languageService.getTranslationByKey('CHATMESSAGEUPDATESUCCESSMESSAGE')
            );
          },
          error: (err) => {
            console.error('Error updating chat message: ', err);
          }
        })
      },
      loadChatMessageReaders(messageId: number): Observable<ChatMessageReaderResource[]> {
        return api.getChatMessageReadersList(store.activeChat(), messageId)
        .pipe(
          catchError((error) => {
            console.error('Error loading chat message readers: ', error);
            return of([]);
          }
        ));
      },
      //#endregion chat message actions
      //#region CRUD Chat
      checkIfChatExists(userIds: number[]): Observable<ChatResource | void> {
        return api.findChat(userIds)
        .pipe(
          take(1),
          catchError((error) => {
            console.error('Error checking if chat exists: ', error);
            return of(void 0);
          })
        );
      },
      startChatWithParticipant(userId: number): void {
        const selfUserId = profile.profile()?.user.id;
        if(selfUserId) {
          const userIds = selfUserId !== userId ? [userId, selfUserId] : [selfUserId];
          this.checkIfChatExists(userIds)
          .subscribe({
            next: (existingChat) => {
              if(existingChat) {
                this.setChatCategory(store.chatListMetadata.category());
                this.setActiveChatAndMessage(existingChat.id, 0);
                router.navigateTo('/chat/detail/' + existingChat.id + '/0');
                this.loadChatDetails();
                return;
              }

              api.createChat({
                custom_name: null,
                custom_avatar: {file_storage_uuid: null},
                user_ids: userIds,
                admin_user_ids: userIds,
                group_ids: []
              })
              .pipe(take(1))
              .subscribe({
                next: (data) => {
                  this.setChatCategory(store.chatListMetadata.category());
                  this.setActiveChatAndMessage(data.id, 0);
                  router.navigateTo('/chat/detail/' + data.id + '/0');
                  this.loadChatDetails();
                },
                error: (error) => {
                  console.error('Error starting chat with participant: ', error);
                }
              })
            },
            error: (error) => {
              console.error('Error starting chat with participant: ', error);
            }
          })
        } else {
          console.error('Error starting chat with participant: missing current user id');
        }
        
      },
      createChat(data: ChatUpdateRequest): void {
        api.createChat(data)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.setChatCategory(store.chatListMetadata.category());
            this.loadChatDetails();
          },
          error: (error) => {
            console.error('Error creating chat: ', error);
          }
        })
      },
      updateChat(chatId: number, data: ChatUpdateRequest): void {
        api.updateChat(chatId, data)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.setChatCategory(store.chatListMetadata.category());
            this.loadChatDetails();
          },
          error: (error) => {
            console.error('Error updating chat: ', error);
          }
        })
      },
      setChatNotification(id: number, isPush: boolean): void {
        api.updateChatNotifications(id, isPush)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.setChatCategory(store.chatListMetadata.category());
            this.loadChatDetails();
          },
          error: (error) => {
            console.error('Error updating chat notifications: ', error);
          }
        })
      },
      deleteChatForSelf(chatId: number): void {
        api.leaveChat(chatId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            router.navigateTo('/chat/detail/0/0');
            this.setChatCategory(store.chatListMetadata.category());
            this.setActiveChatAndMessage(0, 0);
            toasterService.success(
              languageService.getTranslationByKey('CHATSELFDELETIONSUCCESS'),
              languageService.getTranslationByKey('CHATSELFDELETIONSUCCESSMESSAGE')
            );
          },
          error: (error) => {
            console.log('Error deleting chat for self: ', error);
          }
        });
      },
      deleteChatForAll(chatId: number): void {
        api.deleteChat(chatId)
        .pipe(take(1))
        .subscribe({
          next: () => {
            router.navigateTo('/chat/detail/0/0');
            this.setChatCategory(store.chatListMetadata.category());
            this.setActiveChatAndMessage(0, 0);
            toasterService.success(
              languageService.getTranslationByKey('CHATDELETIONSUCCESS'),
              languageService.getTranslationByKey('CHATDELETIONSUCCESSMESSAGE')
            );
          },
          error: (error) => {
            console.log('Error deleting chat for all: ', error);
          }
        });
      },
      updateBlockChat(chatId: number, isBlocked: boolean): void {
        api.updateChatIsBlocked(chatId, isBlocked)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.setChatCategory(store.chatListMetadata.category());
            this.loadChatDetails();
          },
          error: (error) => {
            console.error('Error updating block status of chat: ', error);
          }
        });
      },
      setChatFavoriteStatus(chatId: number, isFavorite: boolean): void {
        api.updateChatIsFavorite(chatId, isFavorite)
        .pipe(take(1))
        .subscribe(() => {
          this.setChatCategory(store.chatListMetadata.category());
        });
      },
      //#endregion CRUD Chat
      //#region local patching
      patchMessageFavorite(messageId: number, favorite: boolean): void {
        const messages = store.activeChatMessages();
        messages.map((message) => {
          if(message.id === messageId) {
            message.favorite = favorite;
          }
          return message;
        });
        patchState(store, (state) => ({
          ...state,
          activeChatMessages: messages
        }));
      },
      patchMessageDeleted(messageId: number){
        const messageFromList = store.activeChatMessages().find((message) => message.id === messageId);
        const messageFromLatest = store.chatList().find((chat) => chat.id === store.activeChat())?.latest_message;

        const sourceMessage =
          messageFromList ??
          (messageFromLatest?.id === messageId ? messageFromLatest : null);

        if (!sourceMessage) {
          return;
        }

        const deletedMessage: ChatMessageResource = {
          ...sourceMessage,
          deleted: true,
          message: null,
          file: null,
          emojis: [],
          favorite: false,
        };

        this.patchUpdatedMessage(deletedMessage);
        this.patchMessageToReply(deletedMessage);
        this.patchMessageToLatestMessage(deletedMessage);
      },
      patchUpdatedMessage(updatedMessage: ChatMessageResource): void {
        patchState(store, (state) => {
          let messageFound = false;
          const updatedMessages = state.activeChatMessages.map((message) => {
            if (message.id === updatedMessage.id) {
              messageFound = true;
              return updatedMessage;
            }
            return message;
          });

          if (!messageFound) {
            return state;
          }

          return {
            ...state,
            activeChatMessages: updatedMessages
          };
        });
      },
      patchNewMessages(new_messages: ChatMessageResource[]): void {
        const uniqueIncomingMessages = dedupeMessagesById(new_messages);
        const existingMessageIds = new Set(store.activeChatMessages().map((message) => message.id));
        const uniqueNewMessages = uniqueIncomingMessages.filter((message) => !existingMessageIds.has(message.id));
        if (uniqueNewMessages.length === 0) {
          return;
        }

        const messages = dedupeMessagesById([...store.activeChatMessages(), ...uniqueNewMessages]);
        const latestPatchedMessage = uniqueNewMessages[uniqueNewMessages.length - 1];

        patchState(store, (state) => ({
          ...state,
          activeChatMessages: messages,
          chatList: sortChatListWithPinnedPriority(
            state.chatList.map((chat) =>
              chat.id === state.activeChat
                ? {
                  ...chat,
                  // Active chat is currently open, so the last locally patched message
                  // should not display as unread in the chat list.
                  latest_message: {...latestPatchedMessage, read: true}
                }
                : chat
            )
          )
        }));
      },
      patchMessageEmoji(messageId: number, emoji: string): void {
        const currentUserId = profile.profile()?.user.id;
        
        if (!currentUserId) {
          return;
        }
        
        const updatedMessages = store.activeChatMessages().map((message) => {
          if (message.id === messageId) {
            // Remove any existing emoji from the current user
            const filteredEmojis = message.emojis.filter(
              (e) => e.user.id !== currentUserId
            );
            
            // Add the new emoji if it's not empty
            if (emoji) {
              return {
                ...message,
                emojis: [
                  ...filteredEmojis,
                  {
                    user: {
                      id: currentUserId,
                      first_name: profile.profile()?.user.first_name ?? '',
                      last_name: profile.profile()?.user.last_name ?? '',
                      avatar: profile.profile()?.user.avatar ?? null
                    },
                    emoji: emoji
                  }
                ]
              };
            } else {
              // Just remove the existing emoji without adding a new one
              return {
                ...message,
                emojis: filteredEmojis
              };
            }
          }
          return message;
        });
        
        patchState(store, (state) => ({
          ...state,
          activeChatMessages: updatedMessages
        }));
      },
      checkPatchForLatestMessage(messages: ChatMessageResource[]): void {
        if (store.activeChat() === 0 || messages.length === 0) {
          return;
        }

        const newestMessage = messages.reduce((newest, current) => {
          const newestTimestamp = new Date(newest.created_at).getTime() || 0;
          const currentTimestamp = new Date(current.created_at).getTime() || 0;
          return currentTimestamp > newestTimestamp ? current : newest;
        });

        const activeChatItem = store.chatList().find((chat) => chat.id === store.activeChat());
        if (!activeChatItem) {
          return;
        }

        const activeLatestTimestamp = new Date(activeChatItem.latest_message?.created_at ?? '').getTime() || 0;
        const newestMessageTimestamp = new Date(newestMessage.created_at).getTime() || 0;
        const isCurrentLatestMessage = activeChatItem.latest_message?.id === newestMessage.id;

        if (isCurrentLatestMessage || newestMessageTimestamp > activeLatestTimestamp) {
          this.patchMessageToLatestMessage(newestMessage);
        }
      },
      patchMessageToLatestMessage(updatedMessage: ChatMessageResource): void {
        patchState(store, (state) => {
          const activeChatItem = state.chatList.find((chat) => chat.id === state.activeChat);
          if (!activeChatItem) {
            return state;
          }

          const currentLatestTimestamp = new Date(activeChatItem.latest_message?.created_at ?? '').getTime() || 0;
          const updatedTimestamp = new Date(updatedMessage.created_at).getTime() || 0;
          const isCurrentLatestMessage = activeChatItem.latest_message?.id === updatedMessage.id;
          const isUpdatedMessageNewer = updatedTimestamp > currentLatestTimestamp;

          if (!isCurrentLatestMessage && !isUpdatedMessageNewer) {
            return state;
          }

          return {
            ...state,
            chatList: sortChatListWithPinnedPriority(
              state.chatList.map((chat) =>
                chat.id === state.activeChat
                  ? {
                    ...chat,
                    latest_message: updatedMessage
                  }
                  : chat
              )
            )
          };
        });
      },
      patchMessageToReply(updatedMessage: ChatMessageResource): void {
        patchState(store, (state) => {
          const hasReplyReferences = state.activeChatMessages.some(
            (message) => message.reply_to?.id === updatedMessage.id && message.reply_to !== updatedMessage
          );
          if (!hasReplyReferences) {
            return state;
          }

          return {
            ...state,
            activeChatMessages: state.activeChatMessages.map((message) =>
              message.reply_to?.id === updatedMessage.id
                ? {
                  ...message,
                  reply_to: updatedMessage
                }
                : message
            )
          };
        });
      },
      refreshChatMessageById(messageId: number): void {
        if (store.activeChat() === 0 || messageId <= 0) {
          return;
        }

        api.getChatMessageList(
          store.activeChat(),
          messageId,
          ChatMessageCursorType.Around,
          1,
          ''
        )
        .pipe(take(1))
        .subscribe({
          next: (chatMessagesResponse: HttpResponse<ChatMessageResource[]>) => {
            const chatMessages = chatMessagesResponse.body ?? [];
            const refreshedMessage = chatMessages.find((message) => message.id === messageId);
            if (!refreshedMessage) {
              return;
            }

            this.patchUpdatedMessage(refreshedMessage);
            this.patchMessageToReply(refreshedMessage);
            this.patchMessageToLatestMessage(refreshedMessage);
          },
          error: (error) => {
            console.error('Error refreshing chat message by ID: ', error);
            this.loadChatMessages(null, null);
          }
        });
      },
      //#endregion local patching
      //#region socketTriggers
      handleMessageUpdate(data: {id: number, chat_id: number, was_created: boolean, created_at: string}): void {
        if (!store.chatModuleOpen()) {
          return;
        }

        if(store.activeChat() === data.chat_id) {
              this.reloadChatList();

            if(data.was_created) {
              void this.markChatReadAndReload(data.chat_id, {
                reloadMode: 'newer',
                loadDetails: false,
              });
              return;
            }

            const isMessageInActiveList = store.activeChatMessages().some((message) => message.id === data.id);
            const isMessageReferencedAsReply = store.activeChatMessages().some((message) => message.reply_to?.id === data.id);
            if (isMessageInActiveList || isMessageReferencedAsReply) {
              if (consumePendingLocalReactionUpdate(data.id)) {
                return;
              }
              this.refreshChatMessageById(data.id);
            }
        } else {
          this.reloadChatList();
        }
      },
      handleChatDelete(data: {chat_id: number}): void {
        if(store.activeChat() === data.chat_id) {
          router.navigateTo('/chat/detail/0/0');
          this.setActiveChatAndMessage(0, 0);
        }
        patchState(store, (state) => ({
          ...state,
          chatList: state.chatList.filter((chat) => chat.id !== data.chat_id)
        }));
      },
      handleChatLeave(data: {chat_id: number, user_id: number}): void {
        if(data.user_id === profile.profile()?.user.id) {
          if(store.activeChat() === data.chat_id) {
            router.navigateTo('/chat/detail/0/0');
            this.setActiveChatAndMessage(0, 0);
          }
          patchState(store, (state) => ({
            ...state,
            chatList: state.chatList.filter((chat) => chat.id !== data.chat_id)
          }));
        } else {
          this.handleChatUpdate({id: data.chat_id, was_created: false});
        }
      },
      handleChatUpdate(data: {id: number, was_created: boolean}): void {
        if (!store.chatModuleOpen()) {
          return;
        }

        if(store.activeChat() === data.id) {
          this.loadChatDetails();
        }
        this.reloadChatList();
      }
      //#endregion socketTriggers
    }
  })
)
